import { useContext, ReactElement, cloneElement, isValidElement } from 'react';
import { TranslationContext } from '../context/TranslationContext';
import { format as formatDate } from 'date-fns';
import { enUS as dateFnsEnUS, fr as dateFnsFr } from 'date-fns/locale';
import { TranslationKeyEnum } from '../types/constants';

// Custom hook to use the translation context
export const useTranslation = () => {
  const context = useContext(TranslationContext);

  // Handles translations with string, number, or Date arguments
  const getTranslationString = (key: TranslationKeyEnum, ...args: (string | number | Date)[]) => {
    let translatedText = context.t(key);

    // If no args, just return the translated text
    if (!args || args.length === 0) {
      return translatedText;
    }

    args.forEach((arg, index) => {
      // Check if the argument is a Date object
      if (arg instanceof Date) {
        // Format date based on locale
        const locale = context.locale.startsWith('fr') ? dateFnsFr : dateFnsEnUS;
        const dateStr = formatDate(arg, 'MMMM d, yyyy', { locale });
        translatedText = translatedText.replace(`{${index}}`, dateStr);
      } else {
        translatedText = translatedText.replace(`{${index}}`, String(arg));
      }
    });
    return translatedText;
  };

  // Handles translations that include React elements
  const getTranslationNode = (
    key: TranslationKeyEnum,
    ...args: (string | number | Date | ReactElement)[]
  ) => {
    const translatedText = context.t(key);

    // If no args, just return the translated text
    if (!args || args.length === 0) {
      return translatedText;
    }

    // If we have React elements, we need to split the string into parts
    const parts = [];
    let lastIndex = 0;
    let match;

    // Regular expression to find placeholders like {0}, {1}, etc.
    const regex = /{(\d+)}/g;

    while ((match = regex.exec(translatedText)) !== null) {
      const placeholderIndex = parseInt(match[1], 10);

      // Add text before placeholder
      if (match.index > lastIndex) {
        parts.push(translatedText.substring(lastIndex, match.index));
      }

      // Add the argument (React element or formatted value)
      if (placeholderIndex < args.length) {
        const arg = args[placeholderIndex];

        if (arg instanceof Date) {
          const locale = context.locale.startsWith('fr') ? dateFnsFr : dateFnsEnUS;
          parts.push(formatDate(arg, 'MMMM d, yyyy', { locale }));
        } else {
          parts.push(arg);
        }
      }

      lastIndex = match.index + match[0].length;
    }

    // Add any remaining text
    if (lastIndex < translatedText.length) {
      parts.push(translatedText.substring(lastIndex));
    }

    // Assign keys to React elements in the parts array
    return parts.map((part, index) => {
      if (isValidElement(part)) {
        // Clone element to add key prop
        return cloneElement(part, { key: index });
      }
      // For strings or non-element types, just return
      return part;
    });
  };

  return {
    ...context,
    t: getTranslationString,
    tn: getTranslationNode,
  };
};
